import { IDog, IProfile, MessageTemplating, TimelineNode } from '@jumpstart/core';
import { IDayMessageDialog } from '@jumpstart/core/dist/schemas/messages/day-message';
import AddIcon from '@mui/icons-material/Add';
import BrushIcon from '@mui/icons-material/Brush';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useState } from 'react';
import { collection, getDocs, getFirestore, updateDoc, QueryDocumentSnapshot, deleteField , setDoc, doc, deleteDoc } from "firebase/firestore";
import { useCollection } from 'react-firebase-hooks/firestore';
import { app, db } from '../../../../core/firebase';
import { IDailyMessage, ISubscriber } from '../../schemas';
import MessageView from '../MessageView';
import ProfileInfoView from './ProfileInfoView';


const formStyles: any = {
  input: {
    color: (theme: Theme) => theme.palette.primary.contrastText,
    width: {
      xs: 320,
      sm: 550
    },
  },
  section: {
    '& > *': {
      marginBottom: 4
    }
  }
};


function MessageForm(props: { doc: QueryDocumentSnapshot; data: IDailyMessage; dogDaysOld: number; onSubmitted: () => void; dog: IDog; profile: IProfile }) {
  const { doc, data, dogDaysOld, onSubmitted, dog, profile } = props;
  const [dialogPath, setDialogPath] = useState<null | string>(null);
  const [newResponse, setNewResponse] = useState('');

  const addNewResponse = (setFieldValue: any, values: any, rootValues: any) => {
    if (!dialogPath) {
      console.error('No dialog path');
      return;
    }

    setFieldValue(
      `${dialogPath}.responses`,
      { ...values.responses, ...{ [newResponse]: { body: '', responses: {} } } }
    );
    setDialogPath(null);
    setNewResponse('');
  };

  const handleDeleteResponseKey = (setFieldValue: any, values: any, responseKey: string, parentPath: string) => {
    console.log(values, responseKey);
    let { [responseKey]: x, ...remaining } = values.responses;
    console.log(remaining, x, parentPath);
    setFieldValue(`${parentPath}.responses`, remaining);
  };

  const dialog = (
    d: IDayMessageDialog,
    parent: string,
    values: any,
    handleChange: any,
    setFieldValue: any,
    rootValues: any
  ) => <Grid container direction="column">
    <Grid item container direction="row" justifyContent="flex-start" alignItems="flex-start" wrap="nowrap">
      <TextField
        id={`${parent}.body`}
        name={`${parent}.body`}
        value={values.body}
        onChange={handleChange}
        sx={formStyles.input}
        variant="outlined"
        label={`${parent}.body`}
        placeholder="Body"
        InputProps={{
          endAdornment: <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setFieldValue(
                `${parent}.body`,
                MessageTemplating.process(values.body, dog, profile)
              )}>
              <BrushIcon/>
            </IconButton>
          </InputAdornment>
        }}
        multiline
      />

    </Grid>
    <div style={{ marginLeft: 32, borderLeft: '2px dashed rgb(46,46,46)' }}>
      {!!d && !!d.responses ? Object.keys(d.responses)
        .map((response: string) => <Grid container direction="column" style={{ padding: 12, marginBottom: 24 }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center"
                  style={{ backgroundColor: '#e7e7e7' }}>
              <Typography variant="body2" style={{ marginBottom: 12 }}>{response}</Typography>
              <IconButton size="small" edge="end" aria-label="delete"
                          onClick={() => handleDeleteResponseKey(setFieldValue, values, response, parent)}>
                <DeleteIcon/>
              </IconButton>
            </Grid>
            {
              dialog(
                (d.responses as any)[response],
                `${parent}.responses.${response}`,
                values.responses[response],
                handleChange,
                setFieldValue,
                rootValues
              )
            }
          </Grid>
        ) : null}
      <Collapse in={!dialogPath}>
        <IconButton onClick={() => setDialogPath(parent)}><AddIcon/></IconButton>
      </Collapse>
      <Collapse in={!!dialogPath && dialogPath === parent}>
        <Grid container direction="row">
          <TextField
            value={newResponse}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewResponse(e.target.value)}
            sx={formStyles.input}
            variant="outlined"
            label="Response Key"
          />
          <IconButton onClick={() => addNewResponse(setFieldValue, values, rootValues)}><CheckIcon/></IconButton>
          <IconButton onClick={() => setDialogPath(null)}><CloseIcon/></IconButton>
        </Grid>
      </Collapse>
    </div>
  </Grid>;

  return (
    <Formik
      initialValues={{
        ...data,
        date: moment().add(data.day - dogDaysOld, 'days').toISOString(),
        id: `DAY_${data.day}`
      }}
      onSubmit={values => {
        const updatedDay: IDailyMessage = {
          ...values,
          date: moment().add(values.day - dogDaysOld, 'days').toISOString(),
          id: `DAY_${values.day}`
        };
        // Remove auto field in case this document was auto generated
        updateDoc(doc.ref, { ...updatedDay, auto: deleteField() })
          .then(() => {
            onSubmitted();
          })
          .catch((err: Error) => {
            console.error(err);
          });
      }}
    >
      {
        ({ values, setFieldValue, handleChange, submitCount, errors, touched }) => (
          <Form style={{ maxWidth: 550, }}>
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={formStyles.sections}
            >

            <TextField
              id="event.id"
              name="event.id"
              value={values.event.id}
              onChange={handleChange}
              sx={formStyles.input}
              variant="outlined"
              label="Event ID"
              aria-label="Event ID"
            />
            <TextField
              id="event.name"
              name="event.name"
              value={values.event.name}
              onChange={handleChange}
              sx={formStyles.input}
              variant="outlined"
              label="Event Name"
              aria-label="Event Name"
            />
            <TextField
              id="day"
              name="day"
              value={values.day}
              onChange={handleChange}
              sx={formStyles.input}
              variant="outlined"
              type="number"
              label={`Day (Dog is ${dogDaysOld})`}
              aria-label="Day"
            />

            {dialog(values.dialog, 'dialog', values.dialog, handleChange, setFieldValue, values)}

            <Button variant="contained" type="submit" color="secondary">Finish</Button>

            </Grid>
          </Form>
        )
      }
    </Formik>
  );
}

export interface IMessageScheduler {
  subscriber: ISubscriber;
  dog: IDog;
  profile: IProfile;
  dogDaysOld: number;
}

export default function MessageScheduler(props: IMessageScheduler) {
  const { subscriber, dogDaysOld, dog, profile } = props;
  const [messages] = useCollection(
    collection(getFirestore(app), 'scout9-messages', subscriber.phone, 'messages')
  );
  const [selected, setSelected] = useState<null | QueryDocumentSnapshot>(null);
  const [showProfile, setShowProfile] = useState(false);

  const getNextDay = () => {
    let day: number = -1;
    const activeDays: QueryDocumentSnapshot[] = (messages ? messages.docs : [])
      .filter((d: QueryDocumentSnapshot) => d.get('day') >= dogDaysOld);
    if (activeDays.length === 0) {
      day = dogDaysOld;
    } else {
      console.error('FIX ME!!');
      for (const d of activeDays) {
        let _day = d.get('day');
        if (_day > day) {
          day = _day + 1;
        }
      }
    }
    if (day === -1) {
      throw new Error('Invalid day');
    }
    return day;
  };

  const handleCopy = (d: QueryDocumentSnapshot) => {
    const day = getNextDay();
    const date: moment.Moment = moment().add(day - dogDaysOld, 'days');
    setDoc(doc(db, "scout9-messages", subscriber.phone, 'messages', `${TimelineNode.month(date.month())}_${date.date()}_${date.year()}`), {
      ...d.data(),
      date: date.toISOString(),
      id: `DAY_${day}`,
      event: {
        id: `DAY_${day}`,
        name: `DAY_${day}`
      }
    });
  };

  const addDefault = () => {
    if (!messages) {
      console.error('No messages');
      return;
    }
    const day = getNextDay();
    const date: moment.Moment = moment().add(day - dogDaysOld, 'days');
    console.log(`Creating DAY_${day}`, date.format('l'));
    const emptyMessage: IDailyMessage = {
      uid: profile.id,
      id: `DAY_${day}`,
      event: {
        id: `DAY_${day}`,
        name: `DAY_${day}`
      },
      day: day,
      dialog: {
        body: '',
        responses: {}
      },
      date: date.toISOString()
    };

    setDoc(doc(db, 'scout9-messages', subscriber.phone, 'messages', `${TimelineNode.month(date.month())}_${date.date()}_${date.year()}`), emptyMessage);
  };

  return (
    <React.Fragment>
      <Dialog fullScreen open={!!selected}>
        <DialogActions
          style={{ width: '100%', position: 'absolute' }}
        >
          <IconButton onClick={() => setSelected(null)}><CloseIcon/></IconButton>
        </DialogActions>
        {
          selected ? <React.Fragment>
              <DialogTitle>Day {selected.get('day')} Automated Messages</DialogTitle>
              <Grid container>
                <IconButton onClick={() => setShowProfile(!showProfile)}>
                  {showProfile ? <ExpandLess/> : <ExpandMore/>}
                </IconButton>
              </Grid>
              <Collapse in={showProfile}>
                <ProfileInfoView subscriber={subscriber} dog={dog} profile={profile}/>
              </Collapse>
              <DialogContent>
                <MessageForm doc={selected} data={selected.data() as IDailyMessage} dogDaysOld={dogDaysOld}
                             onSubmitted={() => setSelected(null)} profile={profile} dog={dog}/>
                {
                  selected
                    ? <MessageView subscriber={subscriber} message={selected.data() as IDailyMessage} dog={dog}
                                   profile={profile}/>
                    : null
                }
              </DialogContent>
            </React.Fragment>
            : null
        }
      </Dialog>
      <List style={{ width: '100%' }}>
        {
          (messages ? messages.docs : [])
            .filter((d: QueryDocumentSnapshot) => d.get('day') >= dogDaysOld)
            .sort((a: QueryDocumentSnapshot, b: QueryDocumentSnapshot) => a.get('day') - b.get('day'))
            .map((d: QueryDocumentSnapshot) =>
              <ListItem key={d.id} button onClick={() => setSelected(d)} dense>
                <ListItemText primary={`${d.id} (Day ${d.get('day')})`} secondary={d.get('dialog.body')}/>
                {
                  !d.get('replies') ?
                    <ListItemSecondaryAction>
                      <IconButton aria-label="copy" onClick={() => handleCopy(d)}>
                        <FileCopyIcon/>
                      </IconButton>
                      <IconButton edge="end" aria-label="delete" onClick={() => {
                        deleteDoc(d.ref);
                      }}>
                        <DeleteIcon/>
                      </IconButton>
                    </ListItemSecondaryAction>
                    : null
                }
              </ListItem>
            )
        }
        <ListItem button onClick={addDefault} dense>
          <ListItemText>Add Day</ListItemText>
        </ListItem>
      </List>
    </React.Fragment>
  );
}

